import { useMemo, useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, CalendarClock, PackageCheck, Trash2 } from 'lucide-react';
import { ApiError, api, errorMessage } from '@/lib/api/client';
import type { CountCheckResponse, CountQuestion, PurchaseOrder } from '@/lib/api/types';
import { EmptyState, Page, PageBody, PageHeader, Panel, Stat, StatRow } from '@/ui/Panel';
import { Button } from '@/ui/Button';
import { Badge } from '@/ui/Badge';
import { Callout } from '@/ui/Callout';
import { Field, Input, MoneyInput, QuantityInput, Textarea } from '@/ui/Field';
import { ConfirmDialog } from '@/ui/Dialog';
import { countLabel, money, qty } from '@/lib/format';
import { checkQuantity, quantityForRequest } from '@/lib/quantity';
import {
  INVOICE_NUMBER_MAX_LENGTH,
  INVOICE_NUMBER_TOO_LONG_MESSAGE,
} from '@kk-pos/shared/purchasing';
import { invalidateAfterReceive, poKeys } from '@/features/buying/api';
import {
  DuplicateInvoiceCallout,
  notesWithDuplicateAnswer,
  useDuplicateInvoiceCheck,
} from '@/features/buying/duplicate-invoice';
import { totalsByUnit, unitTotalsText } from '@/features/buying/unit-totals';
import { ProductPicker, VendorSelect, useVendorOptions } from '@/features/buying/parts';
import {
  fetchOrderCandidates,
  openOrdersFromError,
  recordedOnOrderFromError,
  useOrderCandidates,
} from '@/features/buying/invoice/api';
import { WhichOrderStep } from '@/features/buying/invoice/WhichOrderStep';
import type { InvoicePrefill, OpenOrderChoice } from '@/features/buying/invoice/types';
import { useDebouncedValue } from '@/ui/MultiSelect';
import type { PickedProduct } from '@/features/buying/parts';
import {
  CountConfirmation,
  DELIVERY_DATE_WINDOW_DAYS,
  addDays,
  answersToSend,
  countAnswerKey,
  countConfirmationConsequence,
  localToday,
  stockAnswer,
  storeDayLabel,
  unansweredLines,
  unansweredMessage,
  useCountCheck,
} from '@/features/buying/CountConfirmation';

export const Route = createFileRoute('/_app/purchasing/receive')({
  staticData: { crumb: 'Record invoice' },
  validateSearch: (s: Record<string, unknown>): { vendorId?: number } => ({
    vendorId: Number(s.vendorId) > 0 ? Number(s.vendorId) : undefined,
  }),
  component: QuickReceive,
});

type QuickLine = {
  key: string;
  productId: number;
  productName: string;
  /** Raw text of the quantity box; read once more when the requests are built. */
  receivedQtyText: string;
  unitCostCents: number | null;
  unit: 'ea' | 'lb';
  linkedToVendor: boolean;
  expirationDate: string;
  lotNumber: string;
  showLot: boolean;
};

/**
 * Two calls, in order: create the PO, then receive it. They are NOT atomic —
 * if the second fails the first has already committed, so the failure branch
 * hands the operator the stranded PO id and a link instead of pretending
 * nothing happened.
 */
type Phase =
  | { kind: 'idle' }
  | { kind: 'creating' }
  | { kind: 'receiving'; poId: number }
  | { kind: 'stranded'; poId: number; message: string }
  /**
   * TASK-0236 (backstop): the draft exists and its receive asked "which PO?" — a candidate order
   * appeared after the check before submit. Answered here; the draft is not stranded.
   */
  | {
      kind: 'askOrder';
      poId: number;
      orders: OpenOrderChoice[];
      preselectPoId: number | null;
      body: Record<string, unknown>;
    };

function QuickReceive() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const vendors = useVendorOptions();

  const [vendorId, setVendorId] = useState<number | null>(search.vendorId ?? null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<QuickLine[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  // TASK-0230 (owner rule C45): the day the delivery came in. A past day asks, before any order
  // is created, whether to add stock for items counted on or after it.
  // Required, with no default: the paper invoice's own date (replan critique F4).
  const today = localToday();
  const [deliveredOn, setDeliveredOn] = useState('');
  /**
   * Add-to-stock answers by `countAnswerKey` (invoice line + the question shown); unticked
   * (absent) is the default for a line delivered before the count's day.
   */
  const [stockTicks, setStockTicks] = useState<Record<string, boolean>>({});
  /** Questions from the check run just before submit (they win over the live check). */
  const [submitQuestions, setSubmitQuestions] = useState<{
    deliveredOn: string;
    questions: CountQuestion[];
  } | null>(null);
  const [checkingBeforeSubmit, setCheckingBeforeSubmit] = useState(false);
  // TASK-0236 (INC-8): "which PO?" — the vendor's open orders and POs checked in with no invoice.
  /** Candidates the operator said this invoice is NOT for ("A different delivery"). */
  const [differentFrom, setDifferentFrom] = useState<number[]>([]);
  const [pickedOrder, setPickedOrder] = useState<number | null>(null);
  /** Candidates found by the check just before submit (they win over the live list). */
  const [submitCandidates, setSubmitCandidates] = useState<{
    candidates: OpenOrderChoice[];
    preselectPoId: number | null;
  } | null>(null);

  const checks = useMemo(
    () => lines.map((line) => checkQuantity(line.receivedQtyText, line.unit)),
    [lines]
  );
  /** Null while the line's box does not read: shown as a dash, adds nothing. */
  const lineTotal = (index: number): number | null => {
    const quantity = checks[index]?.quantity ?? null;
    return quantity === null
      ? null
      : Math.round(quantity * (lines[index]?.unitCostCents ?? 0));
  };
  const total = lines.reduce((sum, _, i) => sum + (lineTotal(i) ?? 0), 0);

  const lineProblems = lines.map((line, i) => {
    const check = checks[i]!;
    if (check.problem !== null) return `${line.productName}: quantity ${check.problem}.`;
    if (check.quantity <= 0) return `${line.productName}: quantity must be above zero.`;
    return null;
  });
  const problems = lineProblems.filter((problem) => problem !== null);

  const deliveredOnValid =
    /^\d{4}-\d{2}-\d{2}$/.test(deliveredOn) &&
    deliveredOn <= today &&
    deliveredOn >= addDays(today, -DELIVERY_DATE_WINDOW_DAYS);
  const backDated = deliveredOnValid && deliveredOn < today;
  const countCheck = useCountCheck(
    deliveredOnValid ? deliveredOn : today,
    lines.map((line) => line.productId),
    today
  );
  const questionByProduct = new Map<number, CountQuestion>();
  if (backDated) {
    for (const q of countCheck.questions) questionByProduct.set(q.productId, q);
    if (submitQuestions?.deliveredOn === deliveredOn) {
      for (const q of submitQuestions.questions) questionByProduct.set(q.productId, q);
    }
  }
  const askedLines = lines.filter((line) => questionByProduct.has(line.productId));
  /** One per asked line: where its answer is stored, and whether it has a default. */
  const askedKeys = askedLines.map((line) => {
    const question = questionByProduct.get(line.productId)!;
    return {
      productId: line.productId,
      key: countAnswerKey(line.key, question),
      sameDayAsCount: question.sameDayAsCount,
    };
  });
  const unanswered = unansweredLines(askedKeys, stockTicks);
  const leftOutProducts = new Set(
    askedKeys
      .filter((asked) => stockAnswer(asked, stockTicks) === false)
      .map((asked) => asked.productId)
  );
  const leftOutLines = askedLines.filter((line) => leftOutProducts.has(line.productId));
  /** Why the confirm is held, when it is (the screen can change under an open dialog). */
  const confirmHeldBecause = countCheck.pending
    ? 'Still checking which items were counted after this delivery.'
    : unansweredMessage(unanswered.length);
  // Eaches and pounds stay separate figures; a line left out as counted (C45) adds nothing.
  const stocking = totalsByUnit(
    lines.map((line, i) => ({ line, i })),
    ({ line, i }) => ({
      unit: line.unit,
      quantity: leftOutLines.includes(line) ? 0 : (checks[i]?.quantity ?? 0),
    })
  );

  const busy =
    phase.kind === 'creating' || phase.kind === 'receiving' || checkingBeforeSubmit;
  const invoiceNumberTooLong = invoiceNumber.trim().length > INVOICE_NUMBER_MAX_LENGTH;
  // TASK-0232: warn when this vendor already has a live order with the same invoice number.
  const dup = useDuplicateInvoiceCheck({ vendorId, invoiceNumber });
  const vendorName = vendors.data?.find((v) => v.id === vendorId)?.name ?? null;
  // TASK-0236: an invoice already recorded on a PO is never entered again here (hard stop).
  const recordedHit =
    dup.matches.find((match) => match.kind === 'recorded_invoice') ?? null;
  const debouncedNumber = useDebouncedValue(invoiceNumber.trim(), 400);
  const liveCandidates = useOrderCandidates({
    vendorId,
    productIds: lines.map((line) => line.productId),
    invoiceNumber: debouncedNumber,
  });
  const candidates: OpenOrderChoice[] =
    submitCandidates?.candidates ?? liveCandidates.data?.candidates ?? [];
  const preselect =
    submitCandidates?.preselectPoId ?? liveCandidates.data?.preselectPoId ?? null;
  const selectedOrder =
    pickedOrder !== null && candidates.some((c) => c.poId === pickedOrder)
      ? pickedOrder
      : preselect;
  const unansweredOrders = candidates.filter((c) => !differentFrom.includes(c.poId));
  const ready =
    vendorId !== null &&
    lines.length > 0 &&
    problems.length === 0 &&
    deliveredOnValid &&
    unanswered.length === 0 &&
    !busy &&
    !countCheck.pending &&
    !invoiceNumberTooLong &&
    recordedHit === null &&
    unansweredOrders.length === 0;

  /** "Enter on PO #612": the typed header and lines go to that PO's invoice screen. */
  const enterOnOrder = (poId: number) => {
    const prefill: InvoicePrefill = {
      invoiceNumber: invoiceNumber.trim(),
      deliveredOn,
      lines: lines.map((line) => ({
        productId: line.productId,
        productName: line.productName,
        unit: line.unit,
        qtyText: line.receivedQtyText,
        unitCostCents: line.unitCostCents,
        linkPackSize: null,
      })),
    };
    void navigate({
      to: '/purchasing/$id/invoice',
      params: { id: String(poId) },
      search: { mode: 'manual' },
      state: (prev) => ({ ...prev, invoicePrefill: prefill }),
    });
  };

  const addLine = (product: PickedProduct) =>
    setLines((prev) => [
      ...prev,
      {
        key: `${product.id}-${prev.length}`,
        productId: product.id,
        productName: product.name,
        receivedQtyText: '1',
        unitCostCents: product.vendorUnitCost ?? product.cost ?? null,
        unit: product.unit,
        linkedToVendor: product.linkedToVendor,
        expirationDate: '',
        lotNumber: '',
        showLot: false,
      },
    ]);

  const patch = (index: number, next: Partial<QuickLine>) =>
    setLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, ...next } : line))
    );

  async function submit() {
    if (vendorId === null || lines.length === 0) return;
    setConfirmOpen(false);

    // The one conversion from typed text: both requests carry these numbers.
    let quantities: number[];
    try {
      quantities = lines.map((line) =>
        quantityForRequest(line.receivedQtyText, line.unit, line.productName)
      );
    } catch (error) {
      toast.error(errorMessage(error));
      return;
    }
    if (quantities.some((quantity) => quantity <= 0)) {
      toast.error('Every line needs a quantity above zero. Nothing was sent.');
      return;
    }

    // TASK-0230 review F2: nothing goes out while the live check for the date on screen is still
    // running or a line shown is unanswered.
    try {
      answersToSend(
        askedKeys,
        (asked) => stockAnswer(asked, stockTicks),
        countCheck.pending
      );
    } catch (error) {
      toast.error(errorMessage(error));
      return;
    }

    // TASK-0230: ask the count question BEFORE creating the order, so a back-dated invoice never
    // strands a draft PO on a refusal. A question the screen has not shown yet stops here.
    let decisions: { productId: number; addStock: boolean }[] = [];
    if (backDated) {
      setCheckingBeforeSubmit(true);
      let fresh: CountCheckResponse;
      try {
        fresh = await api.post<CountCheckResponse>('/purchasing/count-check', {
          receiptDate: deliveredOn,
          productIds: lines.map((line) => line.productId),
        });
      } catch (error) {
        setCheckingBeforeSubmit(false);
        toast.error(
          `Could not check the counts: ${errorMessage(error)}. Nothing was sent.`
        );
        return;
      }
      setCheckingBeforeSubmit(false);
      // Seen = the same question was on screen: same product, delivery day and count. A newer
      // count, or one that turns the line into a same-day question, is new (review F2 d).
      const shown = new Map(askedKeys.map((asked) => [asked.key, asked]));
      const lineKeyOf = new Map(lines.map((line) => [line.productId, line.key]));
      const freshKeys = fresh.questions.flatMap((q) => {
        const lineKey = lineKeyOf.get(q.productId);
        return lineKey === undefined
          ? []
          : [
              {
                productId: q.productId,
                key: countAnswerKey(lineKey, q),
                sameDayAsCount: q.sameDayAsCount,
              },
            ];
      });
      const unseen = freshKeys.filter((asked) => !shown.has(asked.key));
      if (unseen.length > 0) {
        setSubmitQuestions({ deliveredOn, questions: fresh.questions });
        toast.message(
          `${countLabel(unseen.length, 'item')} counted after this delivery. Choose for each, then record again. Nothing was sent.`
        );
        return;
      }
      try {
        decisions = answersToSend(
          freshKeys,
          (asked) => stockAnswer(asked, stockTicks),
          false
        ).map(({ line, addStock }) => ({ productId: line.productId, addStock }));
      } catch (error) {
        toast.error(errorMessage(error));
        return;
      }
    }

    // TASK-0236: ask "which PO?" BEFORE creating the draft, with the typed items for the score,
    // so a refusal on the receive never strands a draft. A candidate not answered stops here.
    setCheckingBeforeSubmit(true);
    let fresh: { candidates: OpenOrderChoice[]; preselectPoId: number | null };
    try {
      fresh = await fetchOrderCandidates({
        vendorId,
        productIds: lines.map((line) => line.productId),
        invoiceNumber,
      });
    } catch (error) {
      setCheckingBeforeSubmit(false);
      toast.error(
        `Could not check this vendor's open orders: ${errorMessage(error)}. Nothing was sent.`
      );
      return;
    }
    setCheckingBeforeSubmit(false);
    const unanswered409 = fresh.candidates.filter((c) => !differentFrom.includes(c.poId));
    if (unanswered409.length > 0) {
      setSubmitCandidates(fresh);
      toast.message(
        `${vendorName ?? 'This vendor'} has an order this may be for. Choose, then record again. Nothing was sent.`
      );
      return;
    }
    const acknowledgeOpenOrderIds = fresh.candidates.map((c) => c.poId);

    setPhase({ kind: 'creating' });

    // What the person typed, then (when the invoice number is already on an order) their answer.
    const poNotes = notesWithDuplicateAnswer(notes, dup.noteLine);

    let po: PurchaseOrder;
    try {
      po = await api.post<PurchaseOrder>('/vendors/purchase-orders', {
        vendorId,
        ...(poNotes ? { notes: poNotes } : {}),
        items: lines.map((line, i) => ({
          productId: line.productId,
          orderedQty: quantities[i]!,
          unitCost: line.unitCostCents ?? null,
          unit: line.unit,
        })),
      });
    } catch (error) {
      setPhase({ kind: 'idle' });
      toast.error(`Could not create the purchase order: ${errorMessage(error)}`);
      return;
    }

    void queryClient.invalidateQueries({ queryKey: poKeys.all });
    setPhase({ kind: 'receiving', poId: po.id });

    // Map the server's line ids back onto the cart. The picker refuses
    // duplicate products, so productId is a safe key.
    const byProduct = new Map((po.items ?? []).map((item) => [item.productId, item.id]));
    const receiveItems = lines.flatMap((line, i) => {
      const itemId = byProduct.get(line.productId);
      if (itemId === undefined) return [];
      return [
        {
          id: itemId,
          receivedQty: quantities[i]!,
          ...(line.unitCostCents != null ? { unitCost: line.unitCostCents } : {}),
          ...(line.expirationDate ? { expirationDate: line.expirationDate } : {}),
          ...(line.lotNumber.trim() ? { lotNumber: line.lotNumber.trim() } : {}),
        },
      ];
    });

    const receiveBody: Record<string, unknown> = {
      items: receiveItems,
      ...(invoiceNumber.trim() ? { invoiceNumber: invoiceNumber.trim() } : {}),
      arrivedOn: deliveredOn,
      ...(decisions.length > 0
        ? {
            stockDecisions: decisions.flatMap(({ productId, addStock }) => {
              const itemId = byProduct.get(productId);
              return itemId === undefined ? [] : [{ itemId, addStock }];
            }),
          }
        : {}),
      ...(acknowledgeOpenOrderIds.length > 0 ? { acknowledgeOpenOrderIds } : {}),
    };
    await receiveDraft(po.id, receiveBody);
  }

  /** Receive the draft just created; a refusal keeps the draft's id on screen. */
  async function receiveDraft(poId: number, body: Record<string, unknown>) {
    setPhase({ kind: 'receiving', poId });
    try {
      const received = await api.post<PurchaseOrder>(
        `/vendors/purchase-orders/${poId}/receive`,
        body
      );
      const po = received;
      queryClient.setQueryData(poKeys.detail(po.id), received);
      invalidateAfterReceive(queryClient);
      toast.success(
        received.status === 'received'
          ? `Invoice recorded as PO #${po.id} — fully received.`
          : `Invoice recorded as PO #${po.id} — partially received.`
      );
      // TASK-0252 (INC-5): a cost 3 times or more from what the item usually costs was not recorded
      // (likely a case price); the stock is in and the line waits for its cost on Invoice costs.
      const costWaiting = (received.warnings ?? []).filter(
        (w) => w.kind === 'cost_needs_check'
      );
      if (costWaiting.length > 0) {
        toast.warning(
          `${costWaiting.length === 1 ? '1 cost was' : `${costWaiting.length} costs were`} not recorded: check case vs unit and enter ${costWaiting.length === 1 ? 'it' : 'them'} under Invoice costs.`
        );
      }
      void navigate({
        to: '/purchasing/$id',
        params: { id: String(po.id) },
        search: {
          mode: costWaiting.length > 0 ? ('costs' as const) : ('lines' as const),
        },
      });
    } catch (error) {
      // TASK-0236 (backstop): an order appeared after the check before submit. Ask which; the
      // draft stays and is received here once answered.
      const orders = openOrdersFromError(error);
      if (orders) {
        setPhase({
          kind: 'askOrder',
          poId,
          orders: orders.orders,
          preselectPoId: orders.preselectPoId,
          body,
        });
        return;
      }
      // The PO exists. Say so, loudly, with the id — an operator who does not
      // know this will re-key the whole invoice and double-receive it.
      const recorded = recordedOnOrderFromError(error);
      const message = recorded
        ? `${recorded.message} The draft PO #${poId} was created with your lines and nothing received: cancel it from its page.`
        : error instanceof ApiError && error.isConflict
          ? error.message
          : errorMessage(error);
      setPhase({ kind: 'stranded', poId, message });
    }
  }

  return (
    <Page>
      <PageHeader
        eyebrow={
          <Link to="/purchasing" search={{}} className="hover:text-ink">
            Purchasing
          </Link>
        }
        title="Record an invoice"
        subtitle="Creates a purchase order and receives it in one pass — for goods that arrived without a PO."
        actions={
          <>
            <Button
              size="sm"
              variant="ghost"
              asChild
              icon={<ArrowLeft className="size-3.5" />}
            >
              <Link to="/purchasing" search={{}}>
                Cancel
              </Link>
            </Button>
            <Button
              size="sm"
              variant="primary"
              icon={<PackageCheck className="size-3.5" />}
              disabled={!ready}
              loading={busy}
              onClick={() => setConfirmOpen(true)}
            >
              Record &amp; receive
            </Button>
          </>
        }
      />

      <PageBody>
        {phase.kind === 'askOrder' ? (
          <div className="mb-4 space-y-2">
            <WhichOrderStep
              vendorName={vendorName}
              candidates={phase.orders}
              selectedPoId={pickedOrder ?? phase.preselectPoId}
              onSelect={setPickedOrder}
              orderActionLabel={(id) => `Enter on PO #${id}`}
              quantities={false}
              onUseOrder={enterOnOrder}
              onDifferent={() => {
                const ids = phase.orders.map((order) => order.poId);
                setDifferentFrom((prev) => [...new Set([...prev, ...ids])]);
                void receiveDraft(phase.poId, {
                  ...phase.body,
                  acknowledgeOpenOrderIds: [
                    ...new Set([
                      ...((phase.body.acknowledgeOpenOrderIds as number[] | undefined) ??
                        []),
                      ...ids,
                    ]),
                  ],
                });
              }}
            />
            <p className="text-2xs text-ink-faint">
              Draft PO #{phase.poId} was created with your lines and nothing has been
              received. Enter on another PO leaves it as it is: cancel it from its page.
            </p>
          </div>
        ) : null}

        {phase.kind === 'stranded' ? (
          <div className="border-negative/40 bg-negative-muted mb-4 rounded border p-3">
            <div className="text-ink text-sm font-medium">
              The purchase order was created — the receive was not
            </div>
            <p className="text-ink-muted mt-1 text-xs leading-relaxed">
              PO <span className="font-mono">#{phase.poId}</span> exists with your lines
              on it, but no stock has moved. Do not re-enter this invoice here or you will
              create a second order. Open the PO and receive it there.
            </p>
            <p className="border-line bg-surface text-2xs text-ink-muted mt-2 rounded border p-2 font-mono leading-relaxed whitespace-pre-wrap">
              {phase.message}
            </p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="primary" asChild>
                <Link
                  to="/purchasing/$id"
                  params={{ id: String(phase.poId) }}
                  search={{ mode: 'receive' as const }}
                >
                  Open PO #{phase.poId} to receive
                </Link>
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPhase({ kind: 'idle' })}
              >
                Dismiss
              </Button>
            </div>
          </div>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div className="space-y-4">
            <Panel title="Invoice">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Vendor" required>
                  <VendorSelect
                    value={vendorId}
                    vendors={vendors.data}
                    loading={vendors.isPending}
                    onChange={setVendorId}
                  />
                </Field>
                <Field
                  label="Invoice number"
                  hint="Optional, but worth having for audit."
                  error={
                    invoiceNumberTooLong ? INVOICE_NUMBER_TOO_LONG_MESSAGE : undefined
                  }
                >
                  <Input
                    mono
                    value={invoiceNumber}
                    invalid={invoiceNumberTooLong}
                    onChange={(e) => setInvoiceNumber(e.currentTarget.value)}
                  />
                </Field>
                <Field
                  label="Delivered on"
                  required
                  hint="The date on the delivery slip or invoice"
                  error={
                    deliveredOn === '' || deliveredOnValid
                      ? null
                      : 'Delivered on must be a date in the last 60 days.'
                  }
                >
                  <Input
                    type="date"
                    mono
                    aria-label="Delivered on"
                    value={deliveredOn}
                    min={addDays(today, -DELIVERY_DATE_WINDOW_DAYS)}
                    max={today}
                    invalid={deliveredOn !== '' && !deliveredOnValid}
                    onChange={(e) => {
                      setDeliveredOn(e.currentTarget.value);
                      setSubmitQuestions(null);
                    }}
                  />
                </Field>
              </div>
              {recordedHit ? (
                <Callout
                  tone="negative"
                  className="mt-3"
                  title={`Invoice ${invoiceNumber.trim()} is already recorded on PO #${recordedHit.poId}`}
                  actions={
                    <Button size="xs" asChild>
                      <Link
                        to="/purchasing/$id"
                        params={{ id: String(recordedHit.poId) }}
                        search={{ mode: 'invoices' as const }}
                      >
                        Open PO #{recordedHit.poId}
                      </Link>
                    </Button>
                  }
                >
                  It cannot be entered again here. Open it on that PO.
                </Callout>
              ) : (
                <DuplicateInvoiceCallout
                  check={dup}
                  vendorName={vendorName}
                  invoiceNumber={invoiceNumber}
                  onStop={() => void navigate({ to: '/purchasing', search: {} })}
                  className="mt-3"
                />
              )}
              {candidates.length > 0 ? (
                <WhichOrderStep
                  className="mt-3"
                  vendorName={vendorName}
                  candidates={candidates}
                  selectedPoId={selectedOrder}
                  onSelect={setPickedOrder}
                  differentChosen={unansweredOrders.length === 0}
                  orderActionLabel={(id) => `Enter on PO #${id}`}
                  quantities={false}
                  onUseOrder={enterOnOrder}
                  onDifferent={() =>
                    setDifferentFrom((prev) =>
                      unansweredOrders.length === 0
                        ? []
                        : [...new Set([...prev, ...candidates.map((c) => c.poId)])]
                    )
                  }
                />
              ) : null}
            </Panel>

            {askedLines.length > 0 ? (
              <CountConfirmation
                lines={askedLines.map((line) => {
                  const question = questionByProduct.get(line.productId)!;
                  return {
                    ...question,
                    key: countAnswerKey(line.key, question),
                    name: line.productName,
                  };
                })}
                decisions={stockTicks}
                onChange={(key, addStock) =>
                  setStockTicks((prev) => ({ ...prev, [key]: addStock }))
                }
              />
            ) : null}

            <Panel
              title={`Lines · ${countLabel(lines.length, 'line')}`}
              allowOverflow
              bleed
              footer={
                <div className="flex items-center justify-between">
                  <span>Invoice total</span>
                  <span className="text-ink font-mono text-sm tabular-nums">
                    {money(total)}
                  </span>
                </div>
              }
            >
              <div className="border-line border-b p-3">
                <ProductPicker
                  vendorId={vendorId}
                  disabledIds={new Set(lines.map((line) => line.productId))}
                  onPick={addLine}
                  placeholder={
                    vendorId === null
                      ? 'Pick a vendor first…'
                      : 'Search products on this invoice…'
                  }
                />
              </div>

              {lines.length === 0 ? (
                <EmptyState
                  compact
                  icon={<PackageCheck />}
                  title="Nothing on the invoice yet"
                  description="Add each line as it appears on the paperwork. Quantities here are what physically arrived."
                />
              ) : (
                <div className="divide-line-soft divide-y">
                  {lines.map((line, index) => (
                    <div key={line.key} className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="text-ink min-w-0 flex-1 truncate text-xs">
                          {line.productName}
                          {line.unit === 'lb' ? (
                            <Badge tone="info" className="ml-1.5">
                              lb
                            </Badge>
                          ) : null}
                          {line.linkedToVendor ? null : (
                            <Badge tone="warn" className="ml-1.5">
                              not in vendor catalog
                            </Badge>
                          )}
                          {questionByProduct.has(line.productId) ? (
                            <Badge tone="warn" className="ml-1.5">
                              Last counted{' '}
                              {storeDayLabel(
                                questionByProduct.get(line.productId)!.lastCountedOn
                              )}
                            </Badge>
                          ) : null}
                        </span>
                        <QuantityInput
                          size="sm"
                          className="w-20"
                          unit={line.unit}
                          selectOnFocus
                          aria-label={`Received quantity for ${line.productName}`}
                          value={line.receivedQtyText}
                          invalid={lineProblems[index] != null}
                          onValueChange={(text) =>
                            patch(index, { receivedQtyText: text })
                          }
                        />
                        <MoneyInput
                          size="sm"
                          className="w-24"
                          value={line.unitCostCents}
                          aria-label={`Unit cost for ${line.productName}`}
                          onValueChange={(cents) =>
                            patch(index, { unitCostCents: cents })
                          }
                        />
                        <span className="text-2xs text-ink-muted w-20 shrink-0 text-right font-mono tabular-nums">
                          {lineTotal(index) === null ? '—' : money(lineTotal(index))}
                        </span>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label={`Remove ${line.productName}`}
                          onClick={() =>
                            setLines((prev) => prev.filter((_, i) => i !== index))
                          }
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>

                      {line.showLot ? (
                        <div className="mt-2 flex items-center gap-2">
                          <Input
                            type="date"
                            size="sm"
                            mono
                            className="w-36"
                            aria-label={`Expiration date for ${line.productName}`}
                            value={line.expirationDate}
                            onChange={(e) =>
                              patch(index, { expirationDate: e.currentTarget.value })
                            }
                          />
                          <Input
                            size="sm"
                            className="w-32"
                            placeholder="Lot number"
                            maxLength={128}
                            aria-label={`Lot number for ${line.productName}`}
                            value={line.lotNumber}
                            onChange={(e) =>
                              patch(index, { lotNumber: e.currentTarget.value })
                            }
                          />
                          <button
                            className="text-2xs text-ink-faint hover:text-ink"
                            onClick={() =>
                              patch(index, {
                                showLot: false,
                                expirationDate: '',
                                lotNumber: '',
                              })
                            }
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <button
                          className="text-2xs text-signal-ink mt-1 flex items-center gap-1 hover:underline"
                          onClick={() => patch(index, { showLot: true })}
                        >
                          <CalendarClock className="size-3" /> Add expiry / lot
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {unansweredMessage(unanswered.length) ? (
                <p className="text-2xs text-warn mx-3 mt-3">
                  {unansweredMessage(unanswered.length)}
                </p>
              ) : null}

              {problems.length > 0 ? (
                <ul className="border-negative/30 bg-negative-muted text-2xs text-negative m-3 space-y-1 rounded border p-2">
                  {problems.map((problem, index) => (
                    <li key={index}>{problem}</li>
                  ))}
                </ul>
              ) : null}
            </Panel>
          </div>

          <div className="space-y-4">
            <StatRow className="grid-cols-2 md:grid-cols-2 xl:grid-cols-2">
              <Stat label="Lines" value={qty(lines.length)} />
              <Stat label="Invoice total" value={money(total)} />
            </StatRow>

            <Panel title="Notes">
              <Textarea
                rows={5}
                value={notes}
                placeholder="Anything unusual about the delivery…"
                onChange={(e) => setNotes(e.currentTarget.value)}
              />
            </Panel>

            <Panel title="What this does">
              <ol className="text-2xs text-ink-muted space-y-1.5 leading-relaxed">
                <li>1 · Creates a purchase order with these lines as ordered.</li>
                <li>2 · Immediately receives all of them, moving stock and cost.</li>
                <li>
                  If the receiving step does not go through, the order is still saved and
                  you are shown its number so you can finish receiving it there. Never
                  type the invoice in a second time.
                </li>
              </ol>
            </Panel>
          </div>
        </div>
      </PageBody>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Record and receive this invoice?"
        confirmLabel="Record & receive"
        variant="primary"
        loading={busy}
        confirmDisabled={confirmHeldBecause !== null}
        consequences={[
          `Creates a purchase order with ${countLabel(lines.length, 'line')} worth ${money(total)}.`,
          `Adds ${unitTotalsText(stocking, 'nothing')} to on-hand inventory.`,
          ...(countConfirmationConsequence(leftOutLines.length)
            ? [countConfirmationConsequence(leftOutLines.length)!]
            : []),
          'Updates product cost, or parks a pending cost change when the move is above the approval threshold.',
          ...(dup.noteLine ? [`The order's notes will say “${dup.noteLine}”.`] : []),
        ]}
        onConfirm={() => void submit()}
      >
        {confirmHeldBecause ? (
          <p className="text-2xs text-warn">{confirmHeldBecause}</p>
        ) : null}
      </ConfirmDialog>
    </Page>
  );
}
